import { useState } from "react";
import { toast } from "sonner";

/**
 * ImpersonationBanner - Shows when an admin is impersonating a user
 * Displays at the top of the page with option to exit impersonation
 */
export default function ImpersonationBanner() {
  const [exiting, setExiting] = useState(false);
  
  const isImpersonating = localStorage.getItem("impersonating") === "true";
  const impersonatedUser = localStorage.getItem("user");
  const originalAdminUser = localStorage.getItem("original_admin_user");
  
  if (!isImpersonating) {
    return null;
  }

  const userEmail = impersonatedUser ? JSON.parse(impersonatedUser).email : "Unknown User";
  const adminEmail = originalAdminUser ? JSON.parse(originalAdminUser).email : "Admin";

  const handleExitImpersonation = () => {
    setExiting(true);
    
    try {
      // Restore original admin session
      const originalToken = localStorage.getItem("original_admin_token");
      const originalUser = localStorage.getItem("original_admin_user");
      
      // Clean up impersonation data
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("impersonating");
      localStorage.removeItem("impersonation_session_id");
      localStorage.removeItem("original_admin_token");
      localStorage.removeItem("original_admin_user");
      
      // Restore admin token if available
      if (originalToken) {
        localStorage.setItem("admin_token", originalToken);
      }
      if (originalUser) {
        localStorage.setItem("admin_user", originalUser);
      }
      
      toast.success("Exited impersonation mode");
      
      // Redirect back to admin users page
      window.location.href = "/admin/users";
    } catch (error) {
      toast.error("Failed to exit impersonation");
      setExiting(false);
    }
  };

  return (
    <div 
      className="fixed top-0 left-0 right-0 z-[100] bg-indigo-600 text-white shadow-lg"
      data-testid="impersonation-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span className="font-medium">Impersonation Mode</span>
          </div>
          <span className="text-indigo-200">|</span>
          <span className="text-sm">
            Viewing as <strong>{userEmail}</strong>
          </span>
          <span className="text-indigo-200 text-sm">
            (logged in as {adminEmail})
          </span>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-xs bg-indigo-700 px-3 py-1 rounded-full">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>All actions are logged</span>
          </div>
          
          <button
            onClick={handleExitImpersonation}
            disabled={exiting}
            className="px-4 py-1.5 bg-white text-indigo-600 font-medium rounded-md hover:bg-indigo-50 transition disabled:opacity-50 text-sm flex items-center gap-2"
            data-testid="exit-impersonation-btn"
          >
            {exiting ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Exiting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Exit Impersonation
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
